import { ILocalityReview } from "~/review/interfaces";
import { icon, node } from "~/util/dom";
import StarsBanner from "../stars-banner";
import { CONFIG } from "~/config";
import { getBadgeComposedComponent, reviewGetBadgeColor } from "~/util/review";
import { getIcon } from "~/util/providers/poiagg";
import { createDeepLTranslate } from "../deepl/review";

function getImageData(data: ILocalityReview) {
	const imgData = data.description.media?.urls || {
		default: "",
	};
	let isIcon = false;
	let imgSrc = "";

	if (imgData.default) {
		imgSrc = Mapy.CDN.setSize(imgData.default, Mapy.CDN.SIZE_400_225);
	} else {
		isIcon = true;
		imgSrc = getIcon(data.description.typeId, { scale: 2 });
	}

	return {
		isIcon,
		imgSrc,
	};
}

function getBadgeElem(data: ILocalityReview) {
	return node(getBadgeComposedComponent(), {
		type: reviewGetBadgeColor(data),
		size: "small",
		rating: data.review_rating_stars,
		reviews: data.total,
		lang: CONFIG.defaultLang,
		text: data.review_rating_caption,
	});
}

function createDetailPoiItem(data: ILocalityReview, onClick: (data: ILocalityReview) => void) {
	const review = data.reviews[0];
	const container = node("div", { className: "reviewDetailPois__listItem" });
	const poiPart = node("a", { className: "reviewDetailPois__poiPart", href: `https://${CONFIG.BASE_URL}/?source=${data.source}&id=${data.id}` });
	const separatorPart = node("div", { className: "reviewDetailPois__separatorPart" });
	const reviewPart = node("div", { className: "reviewDetailPois__reviewPart" });

	// poi part
	const imgData = getImageData(data);
	const poiPartDesc = node("div", { className: "reviewDetailPois__poiPartDesc" });
	const poiPartImg = node("div", { className: ["reviewDetailPois__poiPartImg", imgData.isIcon ? "has-icon" : ""].filter(item => item).join(" ") });
	const poiPartTitle = node("h3", { className: "reviewDetailPois__poiPartTitle", textContent: data.description.title });
	const poiPartCategory = node("p", { className: "reviewDetailPois__poiPartCategory", textContent: `${data.description.titleVars?.specification} • ${data.description.titleVars?.locationMain1}` });
	const badge = getBadgeElem(data);

	poiPart.addEventListener("click", event => {
		event.stopPropagation();
		event.preventDefault();
		onClick(data);
	});
	poiPartDesc.append(
		poiPartTitle,
		poiPartCategory,
		badge,
	);
	poiPart.append(
		poiPartDesc,
		poiPartImg,
	);
	poiPartImg.append(
		node("img", { src: imgData.imgSrc, crossOrigin: "anonymous" }),
	);

	// review part
	const reviewPartAvatar = node("mapy-avatar", { className: "reviewDetailPois__reviewPartAvatar", size: 24, premium: review.user.hasPremium ? "mapy" : "none" });
	const reviewPartTextPart = node("div", { className: "reviewDetailPois__reviewPartTextPart" });
	const reviewPartHeader = node("span", { className: `reviewDetailPois__reviewPartHeader` });
	const reviewPartHeaderDate = node("span", { className: `reviewDetailPois__reviewPartHeaderDate`, textContent: Mapy.Util.dateFormat(new Date(review.updated), "d. mn yyyy") });
	const reviewPartText = node("div", { className: "reviewDetailPois__reviewPartText", textContent: review.text });

	if (review.user?.image_url) {
		const userBadge = node("img", { src: review.user.image_url });

		userBadge.crossOrigin = "anonymous";
		reviewPartAvatar.append(userBadge);
	} else {
		reviewPartAvatar.append(icon("avatar-anonym"));
	}

	const starsBanner = new StarsBanner({
		percentage: review.rating,
	}).container;

	// preklad z nebo do originalu, to podle dat
	const deepl = createDeepLTranslate({
		text: Mapy.Util.escapeHTML(review.text),
		lang: review.lang,
		container: reviewPartText,
		reviewId: review.id,
		type: "text",
		native: review.native || !("native" in review),
	}, "review");

	reviewPartHeader.append(
		starsBanner,
		reviewPartHeaderDate,
	);
	reviewPartTextPart.append(
		reviewPartHeader,
		reviewPartText,
		...deepl ? [deepl] : [],
	);
	reviewPart.append(
		reviewPartAvatar,
		reviewPartTextPart,
	);
	container.append(
		poiPart,
		separatorPart,
		reviewPart,
	);

	return container;
}

export interface IDetailPoisAnalytics {
	load: {
		tips: number;
		accommodation: number;
		food: number;
	};
	click: {
		item: ILocalityReview;
		index: number;
	};
	show: {
		item: ILocalityReview;
		index: number;
	};
}

export interface IDetailPoisOpts {
	pois: Array<ILocalityReview>;
	onAnalytics: <T extends keyof IDetailPoisAnalytics>(event: T, data: IDetailPoisAnalytics[T]) => void;
	leftOffset: number;
	jump: number;
	maxItems: number;
}

export default class DetailPois {
	_opts: IDetailPoisOpts;

	_container: HTMLDivElement;

	_list: HTMLDivElement;

	_ind: number;

	_leftBtn: HTMLButtonElement;

	_rightBtn: HTMLButtonElement;

	_showInds: Array<number>;

	constructor(opts: Partial<IDetailPoisOpts> & Pick<IDetailPoisOpts, "pois">) {
		this._opts = {
			pois: [],
			onAnalytics: () => {},
			leftOffset: 16,
			jump: 328,
			maxItems: 5,
			...opts,
		};
		this._opts.pois = this._opts.pois.filter(review => review.description);
		this._container = node("div", { className: "reviewDetailPois" });
		this._list = node("div", { className: "reviewDetailPois__list" });
		this._leftBtn = node("button", { className: "reviewDetailPois__leftBtn" });
		this._rightBtn = node("button", { className: "reviewDetailPois__rightBtn" });
		this._ind = 0;
		this._leftBtn.append(icon("arrows-left"));
		this._rightBtn.append(icon("arrows-right"));
		// omezeni na pocet
		this._opts.pois = this._opts.pois.slice(0, this._opts.maxItems);
		this._showInds = [];

		// ovladaci cudly
		this._leftBtn.addEventListener("click", () => {
			this._changeOffset(-1);
		});
		this._rightBtn.addEventListener("click", () => {
			this._changeOffset(1);
		});
		this._container.append(
			this._leftBtn,
			this._rightBtn,
		);
		this._container.append(this._list);

		this._build();
		this._updateButtons();
	}

	get node() {
		return this._container;
	}

	_changeOffset(direction: number) {
		this._ind = Math.max(0, Math.min(this._ind + direction, this._opts.pois.length - 1));

		// analytika
		if (this._showInds.indexOf(this._ind) === -1) {
			this._opts.onAnalytics("show", {
				item: this._opts.pois[this._ind],
				index: this._ind + 1,
			});
			this._showInds.push(this._ind);
		}

		const left = (-this._ind * this._opts.jump) + (this._ind === 0 ? 0 : this._opts.leftOffset);

		this._list.style.left = `${left}px`;
		this._updateButtons();
	}

	_updateButtons() {
		this._leftBtn.classList[this._ind === 0 ? "add" : "remove"]("hide");
		this._rightBtn.classList[this._ind === this._opts.pois.length - 1 ? "add" : "remove"]("hide");
	}

	_build() {
		const onClick = (item: ILocalityReview) => {
			this._opts.onAnalytics("click", {
				item,
				index: this._opts.pois.indexOf(item) + 1,
			});
		};

		let tips = 0;
		let accommodation = 0;
		let food = 0;

		this._opts.pois.forEach(review => {
			const item = createDetailPoiItem(review, onClick);

			// pro analytiku
			tips += review.category === "tips" ? 1 : 0;
			accommodation += review.category === "accomodation" ? 1 : 0;
			food += review.category === "food" ? 1 : 0;

			this._list.append(item);
		});
		// analytika
		this._opts.onAnalytics("load", {
			tips,
			accommodation,
			food,
		});
		// pri nacteni - zobrazi se prvni poi
		if (this._opts.pois.length) {
			this._opts.onAnalytics("show", {
				item: this._opts.pois[0],
				index: 1,
			});
			this._showInds.push(0);
		}
	}
}
